import { useTRPC } from "@/lib/trpc";

type MediaType = "ugc" | "organization";

// For backward compatibility, define MediaResponse type locally
export type MediaResponse = {
  id: string;
  title: string;
  description: string;
  organizationId: string;
  cloudStorageObjectId: string;
  mediaType?: MediaType;
  cloudStorageObject?: {
    id: string;
    fullUrl: string;
    mimeType?: string | null;
    fileExtension?: string | null;
    public?: boolean;
    uploadConfirmedAt?: Date | null;
  };
  createdAt?: Date;
  updatedAt?: Date;
};

// Create a hook that returns query options for media list
export const useMediaQueryOptions = (_mediaType?: MediaType) => {
  const trpc = useTRPC();

  // Note: TRPC media.list doesn't currently support filtering by mediaType
  return trpc.media.list.queryOptions();
};

// For backward compatibility with existing components
export const mediaQueryOptions = {
  queryKey: ["media"],
  queryFn: async () => {
    // This is a placeholder - components should migrate to useMediaQueryOptions
    console.warn(
      "mediaQueryOptions is deprecated. Use useMediaQueryOptions instead.",
    );
    return [] as MediaResponse[];
  },
};

export const mediaItemQueryOptions = (mediaId: string) => ({
  queryKey: ["media", mediaId],
  queryFn: async () => {
    // This is a placeholder for backward compatibility
    console.warn("mediaItemQueryOptions is deprecated. Use TRPC directly.");
    return {} as MediaResponse;
  },
});

// These functions are deprecated and should be removed once all components use TRPC
export const fetchMedia = async (): Promise<MediaResponse[]> => {
  console.warn("fetchMedia is deprecated. Use TRPC media.list query instead.");
  return [];
};

export const fetchMediaItem = async (
  _mediaId: string,
): Promise<MediaResponse> => {
  console.warn("fetchMediaItem is deprecated. Use TRPC directly.");
  return {} as MediaResponse;
};

export const updateMedia = async ({
  id: _mediaId,
  ..._media
}: Partial<MediaResponse>): Promise<MediaResponse> => {
  console.warn("updateMedia is deprecated. Use TRPC mutations instead.");
  throw new Error("updateMedia is not implemented. Use TRPC mutations.");
};

// biome-ignore lint/suspicious/noExplicitAny: deprecated function
export const createMedia = async (_media: any): Promise<MediaResponse> => {
  console.warn(
    "createMedia is deprecated. Use TRPC media.confirm mutation instead.",
  );
  throw new Error("createMedia is not implemented. Use TRPC mutations.");
};

export const deleteMedia = async (mediaId: string): Promise<void> => {
  console.warn("deleteMedia is deprecated. Use TRPC mutations instead.");

  // For backwards compatibility, we'll make the API call but recommend using TRPC
  const response = await fetch(`/api/media/${mediaId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to delete media: ${response.statusText}`);
  }
};
